﻿import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Car, ChevronDown, ChevronUp, Phone, Users } from "lucide-react";
import type { Customer } from "@/hooks/customers/useCustomerData";
import type { Vehicle } from "@/hooks/customers/useCustomerData";
import {
  
  
  getCustomerMetrics } from "@/hooks/customers/useCustomerData";
import { ExpandedCustomerRow } from "@/components/customers/ExpandedCustomerRow";

export function CustomerList({
  filteredCustomers,
  vehicles,
  orders,
  isCompact,
  setEditingCustomer,
  setAddingVehicleTo,
  setEditingVehicle,
}: {
  filteredCustomers: Customer[];
  vehicles: Vehicle[];
  orders: any[];
  isCompact: boolean;
  setEditingCustomer: (c: Customer) => void;
  setAddingVehicleTo: (id: string) => void;
  setEditingVehicle: (v: Vehicle) => void;
}) {
  const [expandedId, setExpandedId] = useState<string | null>(null);

  if (filteredCustomers.length === 0) {
    return (
      <div className="text-center py-12 bg-slate-900/50 rounded-xl border border-slate-800 border-dashed">
        <Users className="mx-auto text-slate-600 mb-3" size={48} />
        <p className="text-slate-400 text-lg">Nenhum cliente encontrado.</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <AnimatePresence>
        {filteredCustomers.map((customer) => {
          const isExpanded = expandedId === customer.id;
          const custVehicles = vehicles.filter(
            (v) => v.customer_id === customer.id,
          );
          const metrics = getCustomerMetrics(customer.id, orders);
          const TagIcon = metrics.tag.icon;

          return (
            <motion.div
              layout
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95 }}
              transition={{ duration: 0.2 }}
              key={customer.id}
              className="bg-slate-900 border border-slate-800 rounded-xl overflow-hidden shadow-sm transition-all hover:border-slate-700"
            >
              <div
                className={`flex flex-col md:flex-row justify-between items-start md:items-center cursor-pointer gap-4 ${
                  isCompact ? "p-3" : "p-5"
                }`}
                onClick={() => setExpandedId(isExpanded ? null : customer.id)}
              >
                <div className="flex items-center gap-4 flex-1">
                  <div
                    className={`rounded-full bg-slate-800 flex justify-center items-center text-slate-300 font-bold border border-slate-700 ${
                      isCompact ? "w-8 h-8 text-sm" : "w-12 h-12 text-xl"
                    }`}
                  >
                    {customer.name.charAt(0).toUpperCase()}
                  </div>
                  <div>
                    <h3
                      className={`font-bold text-white ${isCompact ? "text-base" : "text-lg"}`}
                    >
                      {customer.name}
                    </h3>
                    <div
                      className={`flex flex-wrap items-center gap-x-4 gap-y-1 text-slate-400 mt-1 ${
                        isCompact ? "text-xs" : "text-sm"
                      }`}
                    >
                      {customer.phone && (
                        <span className="flex items-center gap-1">
                          <Phone size={12} /> {customer.phone}
                        </span>
                      )}
                      <span className="flex items-center gap-1">
                        <Car size={12} /> {custVehicles.length} veículo(s)
                      </span>
                    </div>
                  </div>
                </div>

                <div className="flex items-center gap-6 w-full md:w-auto justify-between md:justify-end">
                  <div
                    className={`flex items-center gap-1.5 px-3 py-1 rounded-full border font-bold ${
                      metrics.tag.color
                    } ${isCompact ? "text-[10px]" : "text-xs"}`}
                  >
                    <TagIcon size={isCompact ? 12 : 14} />
                    {metrics.tag.label}
                  </div>

                  <div className="text-slate-500">
                    {isExpanded ? (
                      <ChevronUp size={isCompact ? 16 : 20} />
                    ) : (
                      <ChevronDown size={isCompact ? 16 : 20} />
                    )}
                  </div>
                </div>
              </div>

              <AnimatePresence>
                {isExpanded && (
                  <ExpandedCustomerRow
                    customer={customer}
                    metrics={metrics}
                    custVehicles={custVehicles}
                    setEditingCustomer={setEditingCustomer}
                    setAddingVehicleTo={setAddingVehicleTo}
                    setEditingVehicle={setEditingVehicle}
                    orders={orders}
                    vehicles={vehicles}
                  />
                )}
              </AnimatePresence>
            </motion.div>
          );
        })}
      </AnimatePresence>
    </div>
  );
}
